<template>
  <div class="app-layout tw:font-sans tw:text-slate-100 tw:min-h-screen tw:relative tw:overflow-x-hidden">

    <!-- Redesigned Sidebar component -->
    <NavSidebar
      :is-pinned="isSidebarPinned"
      v-model:is-mobile-open="isMobileOpen"
      v-model:editing="isSidebarEditing"
      @toggle-pin="toggleSidebarPin"
    />

    <!-- Main Content Wrapper -->
    <div
      ref="contentWrapper"
      class="tw:flex-1 tw:flex tw:flex-col tw:min-w-0 tw:min-h-0 tw:h-screen tw:overflow-y-auto content-transition"
      :class="[
        isSidebarPinned ? 'tw:md:ml-[260px]' : 'tw:md:ml-[72px]'
      ]"
    >
      <!-- Redesigned Header component -->
      <AppHeader
        :is-pinned="isSidebarPinned"
        :is-scrolled="isContentScrolled"
        :active-page="activePage"
        @toggle-mobile="isMobileOpen = !isMobileOpen"
        @customize-sidebar="startSidebarCustomize"
      />

      <!-- Main Content Area -->
      <main class="tw:flex-1 tw:relative tw:z-10 tw:w-full tw:min-h-0">
        <UpsellingPageBanner />
        <SubscriptionAlertBar/>
        <div :class="mainPaneClasses">
          <WhatsNewBanner @action="runWhatsNewAction" />
          <router-view v-slot="{ Component }">
            <keep-alive :include="includeInKeepAlive">
              <component :is="Component" />
            </keep-alive>
          </router-view>
        </div>
      </main>
    </div>

    <ConsoleTourHost />
    <WhatsNewPanel @action="runWhatsNewAction" />
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, provide } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useRouterViewMeta } from '@/composables/useRouterViewMeta'
import Header from './Header.vue'
import NavSidebar from './Sidebar/NavSidebar.vue'
import { getActiveSection } from './Sidebar/activeSection'
import SubscriptionAlertBar from './SubscriptionAlertBar.vue'
import UpsellingPageBanner from './UpsellingPageBanner.vue'
import ConsoleTourHost from './tour/ConsoleTourHost.vue'
import WhatsNewBanner from './whats-new/WhatsNewBanner.vue'
import WhatsNewPanel from './whats-new/WhatsNewPanel.vue'
import { useConsoleTourStore } from '@/stores/consoleTour'
import { openSupportChat } from '@/utils/intercom'
import { shouldLoadIntercom } from '@/composables/useIntercomMessenger'

export default {
  name: 'Full',
  setup () {
    const routerViewMeta = useRouterViewMeta()
    const route = useRoute()
    const store = useStore()

    // Sidebar Layout State
    const isPinned = ref(true)
    const isMobileOpen = ref(false)
    const contentWrapper = ref(null)
    const isContentScrolled = ref(false)
    const fullWidthContentRequests = ref(0)

    const updateHeaderScrollState = () => {
      const wrapper = contentWrapper.value
      isContentScrolled.value = !!wrapper && wrapper.scrollTop > 0
    }

    const requestFullWidthContent = () => {
      fullWidthContentRequests.value += 1

      let released = false
      return () => {
        if (released) {
          return
        }

        released = true
        fullWidthContentRequests.value = Math.max(0, fullWidthContentRequests.value - 1)
      }
    }

    provide('requestFullWidthContent', requestFullWidthContent)

    // Load pinned preference on mount
    onMounted(() => {
      const cached = localStorage.getItem('sidebarPinned')
      if (cached !== null) {
        isPinned.value = JSON.parse(cached)
      }

      const wrapper = contentWrapper.value
      if (wrapper) {
        wrapper.addEventListener('scroll', updateHeaderScrollState, { passive: true })
        updateHeaderScrollState()
      }
    })

    onBeforeUnmount(() => {
      const wrapper = contentWrapper.value
      if (wrapper) {
        wrapper.removeEventListener('scroll', updateHeaderScrollState)
      }
    })

    const toggleSidebarPin = () => {
      isPinned.value = !isPinned.value
      localStorage.setItem('sidebarPinned', JSON.stringify(isPinned.value))
    }

    // The orientation tour holds the sidebar open while it runs: collapsed, the nav is 72px of
    // icons with `opacity-0` labels, and a step saying "your old menu is here" would point at
    // nothing readable. Overriding the computed value rather than writing to isPinned means the
    // person's own setting comes back by itself when the tour ends, including if they close the
    // tab mid-tour -- nothing has to remember to restore it.
    const consoleTourStore = useConsoleTourStore()
    // Customizing the sidebar likewise needs its labels, so it holds the sidebar open the same way.
    const isSidebarEditing = ref(false)
    const isSidebarPinned = computed(() => consoleTourStore.isRunning || isSidebarEditing.value || isPinned.value)

    const startSidebarCustomize = () => {
      if (!consoleTourStore.isRunning) {
        isSidebarEditing.value = true
      }
    }

    // What's New calls to action that change the shell rather than going anywhere.
    const runWhatsNewAction = (action) => {
      if (action === 'customizeSidebar') {
        startSidebarCustomize()
      } else if (action === 'openSupportChat' && shouldLoadIntercom(store.getters)) {
        openSupportChat()
      }
    }

    const mainPaneClasses = computed(() => [
      'main-pane',
      'tw:w-full',
      'tw:p-4',
      'tw:md:p-6',
      'tw:lg:p-8',
      fullWidthContentRequests.value > 0
        ? 'tw:max-w-none'
        : 'tw:max-w-7xl'
    ])

    // The console section the current page belongs to; the header breadcrumbs lead with it.
    const activePage = computed(() => getActiveSection(route))

    return {
      ...routerViewMeta,
      isPinned,
      isSidebarPinned,
      isMobileOpen,
      contentWrapper,
      isContentScrolled,
      mainPaneClasses,
      activePage,
      isSidebarEditing,
      toggleSidebarPin,
      startSidebarCustomize,
      runWhatsNewAction
    }
  },
  components: {
    AppHeader: Header,
    NavSidebar,
    SubscriptionAlertBar,
    UpsellingPageBanner,
    ConsoleTourHost,
    WhatsNewBanner,
    WhatsNewPanel
  }
}
</script>

<style scoped>
.app-layout {
  --base-margin-top: 1.5rem;
}
</style>
